/**
 * @file components/dispatch/CaseDocCategoryRow.tsx
 * @description 案件申請文件「分類上傳行」共用展示組件。
 *
 * 依 SOP 申請材料分類，呈現單一分類的卡片行：
 *   - 左側：分類標題、「可多份」標記、補充提示
 *   - 右側：上傳按鈕（單份分類已有檔案時語意改為「替換」）
 *   - 下方：已上傳檔案列表（由呼叫端透過 children 自行渲染，資料模型不限）
 *
 * 在以下兩處共用，確保「新建案件精靈」與「案件詳情 → 文件」分頁視覺/行為一致：
 *   1. apps/web/src/routes/_app/dispatch/new.tsx（暫存待掛載的 StagedDoc）
 *   2. apps/web/src/routes/_app/dispatch/$caseId.tsx（已落庫的 case_documents）
 *
 * 本組件僅負責「分類標題 + 上傳按鈕」這層一致的外殼；具體檔案列表的資料來源
 * 與單行樣式由呼叫端以 children 注入，避免綁死兩處不同的資料結構。
 */

import type { ReactNode } from 'react';
import { Button, Input, Typography, Upload } from 'antd';
import type { UploadProps } from 'antd';
import { UploadCloud } from 'lucide-react';
import type { CaseDocCategory } from '@/lib/case-doc-categories';

interface CaseDocCategoryRowProps {
  /** 分類設定（key/label/multiple/hint） */
  category: CaseDocCategory;
  /**
   * 使用者選取一批檔案的回呼（多選時整批回傳一次；單選時陣列長度為 1）。
   * 本組件已透過 beforeUpload 回傳 false 攔截 antd 內建 XHR，
   * 呼叫端負責 zip 展開（expandZipFiles）與批量直傳 OSS（uploadFiles）。
   */
  onFiles: (files: File[]) => void;
  /** 是否禁用上傳按鈕（全域上傳中 / 其他互鎖狀態） */
  disabled?: boolean;
  /** 上傳按鈕 loading 態 */
  uploading?: boolean;
  /** 單份分類已存在檔案時，按鈕語意改為「替換」 */
  replaceMode?: boolean;
  /** 為 true 時完全隱藏上傳按鈕（如審核已通過 / 非管理者唯讀視角） */
  hideUpload?: boolean;
  /**
   * 序129 內聯化：本分類尚未上傳檔案時，於同一行內聯顯示「未上傳原因」輸入框（必填）。
   * 由呼叫端計算（通常 = 可上傳且本分類無已上傳檔案）；已上傳時傳 false 以隱藏原因框。
   */
  showReason?: boolean;
  /** 未上傳原因當前值（受控） */
  reasonValue?: string;
  /** 未上傳原因輸入變更回呼 */
  onReasonChange?: (value: string) => void;
  /** 已上傳檔案列表（由呼叫端渲染，資料模型不限） */
  children?: ReactNode;
}

/** 單一分類的上傳行（展示組件，無內部狀態） */
export function CaseDocCategoryRow({
  category,
  onFiles,
  disabled = false,
  uploading = false,
  replaceMode = false,
  hideUpload = false,
  showReason = false,
  reasonValue = '',
  onReasonChange,
  children,
}: CaseDocCategoryRowProps) {
  const uploadProps: UploadProps = {
    multiple: category.multiple,
    showUploadList: false,
    disabled,
    beforeUpload: (file, fileList) => {
      // antd 多選時會對批次中每個檔案各觸發一次 beforeUpload；
      // 只在第一個檔案時回呼一次並帶上整批，避免呼叫端重複處理
      if (file === fileList[0]) {
        onFiles(fileList);
      }
      // 回傳 false 阻止 antd Upload 自帶的 XHR 行為，改由呼叫端直傳 OSS
      return false;
    },
  };

  return (
    <div
      style={{
        border: '1px solid #e2e8f0',
        borderRadius: 8,
        padding: 12,
        background: '#ffffff',
      }}
    >
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '200px 1fr auto',
          alignItems: 'center',
          gap: 12,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            whiteSpace: 'nowrap',
          }}
        >
          <Typography.Text strong style={{ minWidth: 'max-content' }}>
            {category.label}
          </Typography.Text>
          {category.multiple && (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              （可多份）
            </Typography.Text>
          )}
          {category.hint && (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              · {category.hint}
            </Typography.Text>
          )}
        </div>
        {/* 序129 內聯化：未上傳原因輸入框（僅本分類尚未上傳時顯示，撐滿中間空間、必填）。
            上傳後由呼叫端傳 showReason=false 隱藏；此處以紅框標示未填的必填態。 */}
        {showReason ? (
          <Input
            style={{ minWidth: 0 }}
            value={reasonValue}
            maxLength={500}
            disabled={disabled}
            status={reasonValue.trim() ? undefined : 'error'}
            onChange={(e) => onReasonChange?.(e.target.value)}
            placeholder={`請說明「${category.label}」未上傳的原因（必填）`}
          />
        ) : (
          // 佔位彈性元素：無原因框時把上傳按鈕推到最右（等同原 justify-between 視覺）
          <div />
        )}
        {!hideUpload && (
          <Upload {...uploadProps}>
            <Button
              size="small"
              icon={<UploadCloud size={14} />}
              disabled={disabled}
              loading={uploading}
            >
              {replaceMode ? '替換檔案' : '上傳檔案'}
            </Button>
          </Upload>
        )}
      </div>
      {children}
    </div>
  );
}
